import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Autoplay from 'embla-carousel-autoplay';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '../components/ui/carousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DESTINATION_LABELS = {
  'ES': 'Espagne', 'FR': 'France', 'IT': 'Italie', 'TR': 'Turquie',
  'US': 'USA', 'DZ': 'Algérie', 'AE': 'Émirats', 'TH': 'Thaïlande'
};

const DestinationCarousel = ({ trips }) => {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);

  const slides = trips.flatMap(trip => {
    const gallery = trip.gallery_images || [];
    const images = [trip.image_url, ...gallery].filter(Boolean);
    return images.slice(0, 2).map(img => ({
      image: img,
      title: trip.title,
      destination: trip.destination,
      country: DESTINATION_LABELS[trip.country_code] || trip.country_code,
      tripId: trip.id,
      price: trip.price_from,
    }));
  });

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on('select', onSelect);
    onSelect();
    return () => api.off('select', onSelect);
  }, [api]);

  const scrollTo = useCallback((index) => api?.scrollTo(index), [api]);
  const scrollPrev = useCallback(() => api?.scrollPrev(), [api]);
  const scrollNext = useCallback(() => api?.scrollNext(), [api]);

  if (slides.length === 0) return null;

  return (
    <div className="relative" data-testid="destination-carousel">
      <Carousel
        setApi={setApi}
        opts={{ loop: true, align: 'start' }}
        plugins={[Autoplay({ delay: 4000, stopOnInteraction: true, stopOnMouseEnter: true })]}
        className="w-full"
      >
        <CarouselContent className="-ml-0">
          {slides.map((slide, index) => (
            <CarouselItem key={index} className="pl-0 basis-full md:basis-1/2 lg:basis-1/3">
              <Link
                to={`/voyage/${slide.tripId}`}
                onClick={() => window.scrollTo(0, 0)}
                data-testid={`carousel-slide-${index}`}
                className="block relative group overflow-hidden rounded-2xl mx-2 aspect-[4/3]"
              >
                <img
                  src={slide.image}
                  alt={slide.destination}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block bg-[#5BC5D4] text-white text-xs font-bold px-3 py-1 rounded-full mb-2">
                    {slide.country}
                  </span>
                  <h3 className="text-white text-lg font-bold leading-tight drop-shadow-lg">
                    {slide.title}
                  </h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-white/80 text-sm">{slide.destination}</span>
                    <div className="text-right leading-tight">
                      <div className="text-[9px] uppercase tracking-wider text-white/70 font-semibold">Prix de base</div>
                      <span className="text-[#5BC5D4] font-bold text-lg">{slide.price}&euro;</span>
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#5BC5D4]/50 rounded-2xl transition-colors duration-300" />
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Navigation buttons */}
      <button
        onClick={scrollPrev}
        data-testid="carousel-prev-btn"
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#1E3A5F] w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 ml-1"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
      </button>
      <button
        onClick={scrollNext}
        data-testid="carousel-next-btn"
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#1E3A5F] w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 mr-1"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
      </button>

      {/* Dots */}
      <div className="flex justify-center gap-1.5 mt-6" data-testid="carousel-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === current ? 'w-8 bg-[#5BC5D4]' : 'w-2 bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default DestinationCarousel;
